import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ConfirmationService, MessageService, SelectItem } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import {
  GainParameter,
  GainParameterType,
  GainParameterValue,
} from '../../../models/gain-parameter.model';
import { Plant } from '../../../models/plant.model';
import { GainParameterService } from '../../../services/gain-parameter.service';
import * as plantActions from '../../../store/actions/plant.actions';
import { selectPlants } from '../../../store/selectors/plant.selector';

const ALL_PLANTS_LABEL = 'Toutes les usines';
// Sentinel used by the factory filter to target values that apply to all plants (factoryFK === null).
const ALL_PLANTS_FILTER = '__all_plants__';

@Component({
  selector: 'app-gain-parameters',
  templateUrl: './gain-parameters.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GainParametersComponent implements OnInit, OnDestroy {
  readonly typeOptions: SelectItem<GainParameterType>[] = [
    { label: 'Décimal', value: 'Decimal' },
    { label: 'Liste', value: 'List' },
  ];

  parameters: GainParameter[] = [];
  selectedUid: string | null = null;
  showDeleted = false;
  loading = false;
  saving = false;

  plantOptions: SelectItem<string | null>[] = [
    { label: ALL_PLANTS_LABEL, value: null },
  ];

  // null = filter not applied
  factoryFilter: string | null = null;
  labelFilter: string | null = null;
  factoryFilterOptions: SelectItem<string>[] = [];
  labelFilterOptions: SelectItem<string>[] = [];
  filteredValues: GainParameterValue[] = [];

  parameterDialogVisible = false;
  editingParameter: GainParameter | null = null;
  parameterForm = this.emptyParameterForm();

  valueDialogVisible = false;
  editingValue: GainParameterValue | null = null;
  valueForm = this.emptyValueForm();

  private destroy$ = new Subject<void>();

  constructor(
    private gainParameterService: GainParameterService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private store: Store,
    private cdr: ChangeDetectorRef,
  ) {}

  get selectedParameter(): GainParameter | null {
    return this.parameters.find((p) => p.uid === this.selectedUid) ?? null;
  }

  ngOnInit(): void {
    this.store.dispatch(plantActions.loadPlants());
    this.store
      .select(selectPlants)
      .pipe(takeUntil(this.destroy$))
      .subscribe((plants: Plant[] | null) => {
        if (!plants) return;
        this.plantOptions = [
          { label: ALL_PLANTS_LABEL, value: null },
          ...plants.map((p) => ({
            label: p.sapDescription || p.sapId,
            value: p.sapId as string | null,
          })),
        ];
        this.factoryFilterOptions = this.plantOptions.map((o) => ({
          label: o.label,
          value: o.value ?? ALL_PLANTS_FILTER,
        }));
        this.cdr.markForCheck();
      });

    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    this.gainParameterService.getAll(this.showDeleted).subscribe({
      next: (parameters) => {
        this.parameters = parameters ?? [];
        if (!this.parameters.some((p) => p.uid === this.selectedUid)) {
          this.selectedUid = this.parameters[0]?.uid ?? null;
        }
        this.loading = false;
        this.refreshValueFilters();
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.loading = false;
        this.showError(err);
      },
    });
  }

  onFactoryFilterChange(value: string | null): void {
    this.factoryFilter = value ?? null;
    this.applyValueFilters();
  }

  onLabelFilterChange(value: string | null): void {
    this.labelFilter = value ?? null;
    this.applyValueFilters();
  }

  // Rebuilds the label options for the selected parameter and drops a label filter that no longer exists.
  private refreshValueFilters(): void {
    const labels = [
      ...new Set((this.selectedParameter?.values ?? []).map((v) => v.label)),
    ].sort((a, b) => a.localeCompare(b));
    this.labelFilterOptions = labels.map((l) => ({ label: l, value: l }));
    if (this.labelFilter !== null && !labels.includes(this.labelFilter)) {
      this.labelFilter = null;
    }
    this.applyValueFilters();
  }

  private applyValueFilters(): void {
    const values = this.selectedParameter?.values ?? [];
    this.filteredValues = values.filter((v) => {
      if (this.factoryFilter !== null) {
        const factory = v.factoryFK ?? ALL_PLANTS_FILTER;
        if (factory !== this.factoryFilter) return false;
      }
      return this.labelFilter === null || v.label === this.labelFilter;
    });
  }

  onShowDeletedChange(checked: boolean): void {
    this.showDeleted = checked;
    this.load();
  }

  selectParameter(parameter: GainParameter): void {
    this.selectedUid = parameter.uid;
    this.refreshValueFilters();
  }

  plantLabel(value: GainParameterValue): string {
    return value.factoryFK
      ? (value.factoryDescription ?? value.factoryFK)
      : ALL_PLANTS_LABEL;
  }

  typeLabel(type: GainParameterType): string {
    return this.typeOptions.find((o) => o.value === type)?.label ?? type;
  }

  // ---- Parameters ----

  openCreateParameter(): void {
    this.editingParameter = null;
    this.parameterForm = this.emptyParameterForm();
    this.parameterDialogVisible = true;
  }

  openEditParameter(parameter: GainParameter): void {
    this.editingParameter = parameter;
    this.parameterForm = {
      name: parameter.name,
      description: parameter.description ?? '',
      type: parameter.type,
    };
    this.parameterDialogVisible = true;
  }

  get canSaveParameter(): boolean {
    return !!this.parameterForm.name.trim() && !this.saving;
  }

  saveParameter(): void {
    if (!this.canSaveParameter) return;

    const name = this.parameterForm.name.trim();
    const description = this.parameterForm.description.trim() || null;
    const editing = this.editingParameter;

    this.saving = true;
    const request$ = editing
      ? this.gainParameterService.update({
          uid: editing.uid,
          name,
          description,
        })
      : this.gainParameterService.create({
          name,
          description,
          type: this.parameterForm.type,
        });

    request$.subscribe({
      next: (saved) => {
        this.saving = false;
        this.parameterDialogVisible = false;
        if (!editing) this.selectedUid = saved.uid;
        this.load();
      },
      error: (err) => {
        this.saving = false;
        this.showError(err);
      },
    });
  }

  confirmDeleteParameter(parameter: GainParameter): void {
    this.confirmationService.confirm({
      message: `Êtes-vous sûr de vouloir supprimer le paramètre « ${parameter.name} » et ses valeurs ?`,
      header: 'Confirmation de suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () =>
        this.runDelete(this.gainParameterService.delete(parameter.uid)),
    });
  }

  // ---- Values ----

  openCreateValue(): void {
    this.editingValue = null;
    this.valueForm = {
      ...this.emptyValueForm(),
      label: this.labelFilter ?? '',
      factoryFK:
        this.factoryFilter === null || this.factoryFilter === ALL_PLANTS_FILTER
          ? null
          : this.factoryFilter,
    };
    this.valueDialogVisible = true;
  }

  openEditValue(value: GainParameterValue): void {
    this.editingValue = value;
    this.valueForm = {
      label: value.label,
      value: value.value,
      factoryFK: value.factoryFK,
    };
    this.valueDialogVisible = true;
  }

  get canSaveValue(): boolean {
    return (
      !!this.valueForm.label.trim() &&
      this.valueForm.value !== null &&
      !this.saving
    );
  }

  saveValue(): void {
    const parameter = this.selectedParameter;
    if (!this.canSaveValue || !parameter) return;

    const label = this.valueForm.label.trim();
    const value = this.valueForm.value as number;
    const factoryFK = this.valueForm.factoryFK;
    const editing = this.editingValue;

    this.saving = true;
    const request$ = editing
      ? this.gainParameterService.updateValue({
          uid: editing.uid,
          factoryFK,
          value,
          label,
        })
      : this.gainParameterService.createValue({
          parameterFK: parameter.uid,
          factoryFK,
          value,
          label,
        });

    request$.subscribe({
      next: () => {
        this.saving = false;
        this.valueDialogVisible = false;
        this.load();
      },
      error: (err) => {
        this.saving = false;
        this.showError(err);
      },
    });
  }

  confirmDeleteValue(value: GainParameterValue): void {
    this.confirmationService.confirm({
      message: `Êtes-vous sûr de vouloir supprimer la valeur « ${value.label} » ?`,
      header: 'Confirmation de suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () =>
        this.runDelete(this.gainParameterService.deleteValue(value.uid)),
    });
  }

  private runDelete(
    request$: ReturnType<GainParameterService['delete']>,
  ): void {
    this.saving = true;
    request$.subscribe({
      next: () => {
        this.saving = false;
        this.load();
      },
      error: (err) => {
        this.saving = false;
        this.showError(err);
      },
    });
  }

  private showError(err: any): void {
    this.messageService.add({
      key: 'globalToast',
      severity: 'error',
      summary: 'Erreur',
      detail: this.extractMessage(err),
    });
    this.cdr.markForCheck();
  }

  private extractMessage(err: any): string {
    const fallback = "Une erreur est survenue lors de l'opération.";
    const body = err?.body;
    if (typeof body === 'string' && body) {
      try {
        const parsed = JSON.parse(body);
        return parsed?.message ?? parsed?.detail ?? parsed?.title ?? body;
      } catch {
        return body;
      }
    }
    return err?.message ?? fallback;
  }

  private emptyParameterForm(): {
    name: string;
    description: string;
    type: GainParameterType;
  } {
    return { name: '', description: '', type: 'Decimal' };
  }

  private emptyValueForm(): {
    label: string;
    value: number | null;
    factoryFK: string | null;
  } {
    return { label: '', value: null, factoryFK: null };
  }
}
